import clsx from 'clsx';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Theme = 'system' | 'light' | 'dark';

/** Light / dark / as the operating system. Stored per browser; without storage it lasts for the visit. */
export function storedTheme(): Theme {
  try {
    const v = localStorage.getItem('pz-theme');
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(theme: Theme) {
  if (theme === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(storedTheme);
  const set = (v: Theme) => {
    setTheme(v);
    applyTheme(v);
    try {
      localStorage.setItem('pz-theme', v);
    } catch {
      /* storage not available */
    }
  };
  const items: { id: Theme; icon: typeof Sun; label: string }[] = [
    { id: 'light', icon: Sun, label: t('nav.themeLight') },
    { id: 'dark', icon: Moon, label: t('nav.themeDark') },
    { id: 'system', icon: Monitor, label: t('nav.themeSystem') },
  ];
  return (
    <div role="group" aria-label={t('nav.theme')} className={clsx('inline-flex rounded-md border border-line p-0.5', className)}>
      {items.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          aria-pressed={theme === id}
          title={label}
          aria-label={label}
          onClick={() => set(id)}
          className={clsx('inline-flex size-6 items-center justify-center rounded', theme === id ? 'bg-accent-soft text-accent' : 'text-graphite hover:text-ink')}
        >
          <Icon className="size-3.5" />
        </button>
      ))}
    </div>
  );
}
